import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  getDatevRuleset,
  upsertDatevRuleset,
  deleteDatevRuleset,
} from '../apis/datevRulesetApi';
import { UpsertDatevRulesetRequest } from '../apis/types';

export const useDatevRuleset = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const {
    data: ruleset = null,
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: ['datevRuleset'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getDatevRuleset(token);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const upsertMutation = useMutation({
    mutationFn: async (data: UpsertDatevRulesetRequest) => {
      const token = await getAccessTokenSilently();
      return upsertDatevRuleset(data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datevRuleset'] });
    },
    onError: error => {
      console.error('Error upserting ruleset:', error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();
      return deleteDatevRuleset(token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datevRuleset'] });
    },
    onError: error => {
      console.error('Error deleting ruleset:', error);
    },
  });

  const error =
    queryError?.message ||
    upsertMutation.error?.message ||
    deleteMutation.error?.message ||
    null;

  return {
    ruleset,
    loading,
    saving: upsertMutation.isPending,
    deleting: deleteMutation.isPending,
    error,
    upsertRuleset: upsertMutation.mutateAsync,
    deleteRuleset: deleteMutation.mutateAsync,
  };
};
